import React, { useState } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import {
	LoadingOverlay,
	TextInput,
	Title,
	Text,
	useMantineTheme,
	PasswordInput,
	Checkbox,
	Notification,
	Button,
	Box,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { AppPaths } from '@/configs/appPaths';
import { useContent } from '@/providers/ContentProvider';
import { ErrorCode } from '@/types/types';
import styles from './Login.module.scss';

export interface LoginValues {
	email: string;
	password: string;
}

export enum SubmitStatus {
	THROTTLE,
	LOADING,
	ERROR,
	SUCCESS,
}

const initialValues = {
	email: '',
	password: '',
};

const baseSlug = 'login.';

export const Login: React.FC = () => {
	const { getErrorMessage, getContent } = useContent();

	const { other } = useMantineTheme();
	const { remember, setRemember, login } = useAuth();

	const [status, setStatus] = useState(SubmitStatus.THROTTLE);
	const [resultMessage, setResultMessage] = useState('');

	const form = useForm({
		initialValues,
		validate: {
			email: (value) => {
				if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
					return getErrorMessage(ErrorCode.INVALID_EMAIL);
				}
				return null;
			},
		},
	});

	const handleSubmit = form.onSubmit(async (loginValues: LoginValues) => {
		setStatus(SubmitStatus.LOADING);
		setResultMessage('');

		const result = await login(loginValues);
		form.reset();

		if (result.status === SubmitStatus.ERROR) {
			setStatus(SubmitStatus.ERROR);
			setResultMessage(getErrorMessage(result.stack));
			return;
		}

		setStatus(SubmitStatus.THROTTLE);
	});

	return (
		<Box className="page-wrapper-unauth">
			<form className={styles.form} onSubmit={handleSubmit}>
				<LoadingOverlay visible={status === SubmitStatus.LOADING} />
				<Title ta="center" order={1} mb="md">
					{getContent(`${baseSlug}title`)}
				</Title>
				<Text size="lg" ta="center" c="dimmed" mb="xl">
					{getContent(`${baseSlug}description`)}
				</Text>
				{status === SubmitStatus.ERROR && (
					<Notification
						{...other.notificationProps.danger}
						mb="lg"
						onClose={() => setStatus(SubmitStatus.THROTTLE)}
					>
						<Text size="md">{resultMessage}</Text>
					</Notification>
				)}
				{status === SubmitStatus.SUCCESS && (
					<Notification
						{...other.notificationProps.success}
						mb="lg"
						onClose={() => setStatus(SubmitStatus.THROTTLE)}
					>
						<Text size="md">{resultMessage}</Text>
					</Notification>
				)}
				<TextInput
					{...form.getInputProps('email')}
					required
					type="email"
					placeholder={getContent(`${baseSlug}emailPlaceholder`)}
					mb="lg"
					size="lg"
				/>
				<PasswordInput
					{...form.getInputProps('password')}
					required
					placeholder={getContent(`${baseSlug}passwordPlaceholder`)}
					mb="lg"
					size="lg"
				/>
				<Checkbox
					label={getContent(`${baseSlug}checkboxLabel`)}
					mb="lg"
					checked={remember}
					onChange={({ target }) => setRemember(target.checked)}
				/>
				<Button type="submit" mb="lg" fullWidth size="lg">
					{getContent(`${baseSlug}submitButton`)}
				</Button>
				<Box ta="center" c="dimmed">
					{getContent(`${baseSlug}navigateResetPassword`, {
						links: {
							$RESET_PASSWORD: {
								to: `/${AppPaths.RESET_PASSWORD}`,
								className: 'injected-anchor',
							},
						},
					})}
				</Box>
			</form>
		</Box>
	);
};
